import React, { useCallback, useEffect, useMemo, useState } from 'react';
import SafeIcon from '../common/SafeIcon';
import * as FiIcons from 'react-icons/fi';
import { auditService } from '../services/auditService';

const {
  FiActivity,
  FiChevronDown,
  FiChevronUp,
  FiFilter,
  FiRefreshCw,
  FiSearch,
  FiShield,
  FiUser
} = FiIcons;

const MODULES = ['All', 'Sales', 'Purchases', 'Accounting', 'Payroll', 'Administration', 'System'];
const ACTIONS = ['All', 'INSERT', 'UPDATE', 'DELETE', 'SYSTEM'];

const formatDate = (value) => {
  if (!value) return 'Unknown time';
  return new Intl.DateTimeFormat('en-US', {
    dateStyle: 'medium',
    timeStyle: 'short'
  }).format(new Date(value));
};

const actionTone = {
  INSERT: 'bg-emerald-50 text-emerald-700 border-emerald-100',
  UPDATE: 'bg-blue-50 text-blue-700 border-blue-100',
  DELETE: 'bg-rose-50 text-rose-700 border-rose-100',
  SYSTEM: 'bg-slate-100 text-slate-600 border-slate-200'
};

const activityLabel = (log) => {
  const names = {
    accounts_20240320: 'business details',
    profiles_20240320: 'profile',
    account_users_20240320: 'team member',
    customers_20240522: 'customer',
    invoices_20240522: 'invoice',
    vendors_20240522: 'vendor',
    bills_20240522: 'bill',
    chart_of_accounts_20240522: 'chart of accounts item',
    transactions_20240522: 'transaction',
    bank_rules_1786065000000: 'bank rule',
    ledger_reconciliations_1787229000000: 'reconciliation',
    payroll_employees_1786090711701: 'employee',
    payroll_runs_1786090711701: 'payroll run'
  };
  const action = {
    INSERT: 'Created',
    UPDATE: 'Updated',
    DELETE: 'Removed',
    SYSTEM: 'Recorded activity for'
  };

  return `${action[log.action] || 'Updated'} ${names[log.table_name] || 'business information'}`;
};

const fieldLabels = {
  description: 'Description',
  payee: 'Payee',
  memo: 'Memo',
  date: 'Date',
  status: 'Status',
  review_status: 'Review status',
  coa_id: 'Category',
  debit: 'Debit',
  credit: 'Credit',
  amount: 'Amount',
  notes: 'Notes',
  name: 'Name',
  email: 'Email',
  role: 'Role'
};

const getActorName = (log) => {
  if (log.actor_name) return log.actor_name;
  if (log.actor_email) return log.actor_email.split('@')[0];
  return 'System process';
};

const formatChangedValue = (value) => {
  if (value === null || value === undefined || value === '') return 'empty';
  if (typeof value === 'boolean') return value ? 'Yes' : 'No';
  if (typeof value === 'object') return 'updated';
  return String(value);
};

const getChangedFields = (log) => {
  const oldData = log.old_data || {};
  const newData = log.new_data || {};

  return Object.keys({ ...oldData, ...newData })
    .filter(field => !['id', 'account_id', 'created_at', 'updated_at'].includes(field))
    .filter(field => JSON.stringify(oldData[field]) !== JSON.stringify(newData[field]))
    .slice(0, 4);
};

const detailedActivity = (log) => {
  const actor = getActorName(log);
  const entity = activityLabel(log).replace(/^(Created|Updated|Removed|Recorded activity for)\s/, '');

  if (log.action === 'INSERT') {
    return `${actor} created the ${entity}.`;
  }

  if (log.action === 'DELETE') {
    return `${actor} removed the ${entity}.`;
  }

  if (log.action !== 'UPDATE') {
    return `${actor} recorded activity for the ${entity}.`;
  }

  const oldData = log.old_data || {};
  const newData = log.new_data || {};
  const fields = getChangedFields(log);

  if (!fields.length) {
    return `${actor} updated the ${entity}.`;
  }

  const changes = fields.map(field => {
    const label = fieldLabels[field] || field.replaceAll('_', ' ');
    return `${label} changed from “${formatChangedValue(oldData[field])}” to “${formatChangedValue(newData[field])}”`;
  });

  return `${actor} updated the ${entity}: ${changes.join('; ')}.`;
};

function SummaryCard({ label, value, tone = 'slate' }) {
  const tones = {
    slate: 'bg-slate-900 text-white',
    blue: 'bg-white border-slate-200 text-slate-900',
    green: 'bg-white border-emerald-100 text-emerald-700',
    violet: 'bg-white border-violet-100 text-violet-700'
  };

  return (
    <div className={`rounded-2xl border p-4 shadow-sm ${tones[tone]}`}>
      <p className={`text-[10px] uppercase tracking-[0.16em] ${tone === 'slate' ? 'text-slate-400' : 'text-slate-400'}`}>
        {label}
      </p>
      <p className="mt-1 text-2xl font-semibold">{value}</p>
    </div>
  );
}

function AuditRow({ log, expanded, onToggle }) {
  const tone = actionTone[log.action] || actionTone.SYSTEM;

  return (
    <div className="border-b border-slate-100 last:border-0">
      <button
        type="button"
        onClick={onToggle}
        className="grid w-full grid-cols-[auto_minmax(0,1fr)_auto] items-center gap-3 px-4 py-3 text-left hover:bg-slate-50"
      >
        <div className="flex h-9 w-9 items-center justify-center rounded-xl bg-slate-100 text-slate-500">
          <SafeIcon icon={log.action === 'DELETE' ? FiShield : FiActivity} />
        </div>
        <div className="min-w-0">
          <div className="flex flex-wrap items-center gap-2">
            <span className="truncate text-sm font-medium text-slate-900">{detailedActivity(log)}</span>
            <span className={`rounded-full border px-2 py-0.5 text-[10px] font-medium ${tone}`}>
              {log.action === 'INSERT' ? 'Created' : log.action === 'DELETE' ? 'Removed' : log.action === 'UPDATE' ? 'Updated' : 'System'}
            </span>
          </div>
          <p className="mt-0.5 truncate text-xs text-slate-500">
            {getActorName(log)} · {log.module || 'General'} · {formatDate(log.created_at)}
          </p>
        </div>
        <SafeIcon icon={expanded ? FiChevronUp : FiChevronDown} className="text-slate-400" />
      </button>

      {expanded && (
        <div className="grid gap-3 bg-slate-50/70 px-4 pb-4 pt-1 md:grid-cols-2">
          <div className="rounded-xl border border-slate-200 bg-white p-3">
            <p className="mb-2 text-[10px] font-medium uppercase tracking-wider text-slate-400">Activity details</p>
            <dl className="space-y-1.5 text-xs">
              <div className="flex justify-between gap-3">
                <dt className="text-slate-400">What changed</dt>
                <dd className="max-w-[75%] text-right text-slate-700">{detailedActivity(log)}</dd>
              </div>
              <div className="flex justify-between gap-3">
                <dt className="text-slate-400">Area</dt>
                <dd className="text-right text-slate-700">{log.module || 'General'}</dd>
              </div>
              <div className="flex justify-between gap-3">
                <dt className="text-slate-400">Actor</dt>
                <dd className="text-right text-slate-700">{getActorName(log)}</dd>
              </div>
            </dl>
          </div>
          <div className="rounded-xl border border-slate-200 bg-white p-3">
            <p className="mb-2 text-[10px] font-medium uppercase tracking-wider text-slate-400">Change details</p>
            <p className="text-xs leading-5 text-slate-600">
              {log.action === 'INSERT'
                ? 'A new item was added to your business records.'
                : log.action === 'DELETE'
                  ? 'An item was removed from your business records.'
                  : log.action === 'UPDATE'
                    ? detailedActivity(log)
                    : 'This change was securely saved to your business records.'}
            </p>
          </div>
        </div>
      )}
    </div>
  );
}

export default function AuditLog() {
  const [logs, setLogs] = useState([]);
  const [search, setSearch] = useState('');
  const [module, setModule] = useState('All');
  const [action, setAction] = useState('All');
  const [expandedId, setExpandedId] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  const loadLogs = useCallback(async () => {
    try {
      setLoading(true);
      setError('');
      const data = await auditService.listLogs({ search, module, action });
      setLogs(data);
    } catch (err) {
      setError(err.message || 'Unable to load audit activity.');
    } finally {
      setLoading(false);
    }
  }, [search, module, action]);

  useEffect(() => {
    const timer = setTimeout(loadLogs, search ? 250 : 0);
    return () => clearTimeout(timer);
  }, [loadLogs, search]);

  const summary = useMemo(() => ({
    total: logs.length,
    updates: logs.filter(log => log.action === 'UPDATE').length,
    people: new Set(logs.map(log => log.actor_id).filter(Boolean)).size,
    modules: new Set(logs.map(log => log.module)).size
  }), [logs]);

  return (
    <div className="mx-auto max-w-[1180px] pb-16 pt-2">
      <style>{`
        div[class*="max-w-[1180px]"] > div:first-of-type {
          margin-bottom: 0.9rem !important;
          padding-bottom: 0.75rem !important;
        }

        div[class*="max-w-[1180px]"] > div:nth-of-type(2) {
          gap: 0.6rem !important;
          margin-bottom: 0.75rem !important;
        }

        div[class*="max-w-[1180px]"] > div:nth-of-type(2) > div {
          border-radius: 0.75rem !important;
          padding: 0.75rem !important;
        }

        div[class*="max-w-[1180px]"] > div:nth-of-type(2) p.text-2xl {
          font-size: 1.25rem !important;
          font-weight: 500 !important;
        }

        div[class*="max-w-[1180px]"] > div:nth-of-type(3) {
          margin-bottom: 0.75rem !important;
          border-radius: 0.75rem !important;
          padding: 0.65rem !important;
        }

        div[class*="max-w-[1180px]"] input,
        div[class*="max-w-[1180px]"] select {
          height: 2.25rem !important;
          min-height: 2.25rem !important;
        }

        div[class*="max-w-[1180px]"] > div:nth-of-type(4) {
          border-radius: 0.75rem !important;
        }

        div[class*="max-w-[1180px]"] > div:nth-of-type(4) > div:first-child {
          padding-top: 0.7rem !important;
          padding-bottom: 0.7rem !important;
        }

        div[class*="max-w-[1180px]"] > div:nth-of-type(4) button {
          padding-top: 0.6rem !important;
          padding-bottom: 0.6rem !important;
        }

        div[class*="max-w-[1180px]"] > div:nth-of-type(4) button > div:first-child {
          width: 2rem !important;
          height: 2rem !important;
          border-radius: 0.55rem !important;
        }

        div[class*="max-w-[1180px]"] > div:nth-of-type(4) button span {
          font-size: 0.8rem !important;
          font-weight: 400 !important;
        }

        div[class*="max-w-[1180px]"] > div:nth-of-type(4) button p {
          font-size: 0.7rem !important;
        }

        div[class*="max-w-[1180px]"] > div:nth-of-type(4) button > div:nth-child(2) > div {
          gap: 0.35rem !important;
        }

        div[class*="max-w-[1180px]"] > div:nth-of-type(4) button > div:nth-child(2) > div > span {
          padding: 0.1rem 0.35rem !important;
          font-size: 0.58rem !important;
        }

        div[class*="max-w-[1180px]"] > div:nth-of-type(4) > div > div {
          border-radius: 0.65rem !important;
        }
      `}</style>
      <div className="mb-5 flex flex-col justify-between gap-3 md:flex-row md:items-end">
        <div>
          <div className="mb-1 flex items-center gap-2 text-[10px] font-medium uppercase tracking-[0.18em] text-emerald-600">
            <span className="h-1.5 w-1.5 rounded-full bg-emerald-500" />
            Governance & compliance
          </div>
          <h1 className="text-2xl font-semibold tracking-tight text-slate-900">Audit Log</h1>
          <p className="mt-1 text-sm text-slate-500">
            Track changes across sales, purchases, accounting, reports, and business administration.
          </p>
        </div>
        <button
          type="button"
          onClick={loadLogs}
          className="inline-flex items-center justify-center gap-2 rounded-xl border border-slate-200 bg-white px-3 py-2 text-xs font-medium text-slate-600 shadow-sm hover:border-emerald-200 hover:text-emerald-700"
        >
          <SafeIcon icon={FiRefreshCw} />
          Refresh
        </button>
      </div>

      <div className="mb-4 grid grid-cols-2 gap-3 lg:grid-cols-4">
        <SummaryCard label="Visible events" value={summary.total} tone="slate" />
        <SummaryCard label="Updates" value={summary.updates} tone="blue" />
        <SummaryCard label="Active users" value={summary.people} tone="green" />
        <SummaryCard label="Modules" value={summary.modules} tone="violet" />
      </div>

      <div className="mb-4 rounded-2xl border border-slate-200 bg-white p-3 shadow-sm">
        <div className="grid gap-2 md:grid-cols-[minmax(0,1fr)_180px_150px_auto]">
          <label className="relative">
            <span className="sr-only">Search audit logs</span>
            <SafeIcon icon={FiSearch} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
            <input
              value={search}
              onChange={event => setSearch(event.target.value)}
              placeholder="Search people, tables, or activity..."
              className="h-10 w-full rounded-xl border border-slate-200 bg-slate-50 pl-9 pr-3 text-xs outline-none focus:border-emerald-300 focus:ring-2 focus:ring-emerald-100"
            />
          </label>
          <label className="relative">
            <span className="sr-only">Filter by module</span>
            <SafeIcon icon={FiFilter} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
            <select
              value={module}
              onChange={event => setModule(event.target.value)}
              className="h-10 w-full appearance-none rounded-xl border border-slate-200 bg-slate-50 pl-9 pr-3 text-xs text-slate-700 outline-none focus:border-emerald-300"
            >
              {MODULES.map(value => <option key={value}>{value}</option>)}
            </select>
          </label>
          <select
            value={action}
            onChange={event => setAction(event.target.value)}
            className="h-10 rounded-xl border border-slate-200 bg-slate-50 px-3 text-xs text-slate-700 outline-none focus:border-emerald-300"
          >
            {ACTIONS.map(value => <option key={value}>{value}</option>)}
          </select>
          <div className="hidden items-center justify-center rounded-xl bg-slate-50 px-3 text-xs text-slate-400 md:flex">
            <SafeIcon icon={FiUser} className="mr-1.5" />
            Workspace activity
          </div>
        </div>
      </div>

      <div className="overflow-hidden rounded-2xl border border-slate-200 bg-white shadow-sm">
        <div className="flex items-center justify-between border-b border-slate-100 px-4 py-3">
          <div>
            <h2 className="text-sm font-medium text-slate-900">Activity history</h2>
            <p className="text-xs text-slate-400">Database-recorded changes for the active business.</p>
          </div>
          <span className="text-xs text-slate-400">{logs.length} shown</span>
        </div>

        {loading ? (
          <div className="flex h-48 items-center justify-center text-sm text-slate-400">
            Loading audit history...
          </div>
        ) : error ? (
          <div className="p-8 text-center">
            <p className="text-sm text-rose-600">{error}</p>
            <button type="button" onClick={loadLogs} className="mt-3 text-xs font-medium text-emerald-700 hover:underline">
              Try again
            </button>
          </div>
        ) : logs.length ? (
          logs.map(log => (
            <AuditRow
              key={log.id}
              log={log}
              expanded={expandedId === log.id}
              onToggle={() => setExpandedId(expandedId === log.id ? null : log.id)}
            />
          ))
        ) : (
          <div className="flex h-48 flex-col items-center justify-center px-6 text-center">
            <SafeIcon icon={FiActivity} className="mb-2 text-2xl text-slate-300" />
            <p className="text-sm font-medium text-slate-600">No audit events found</p>
            <p className="mt-1 text-xs text-slate-400">New database changes will appear here automatically.</p>
          </div>
        )}
      </div>
    </div>
  );
}